import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import {
  resolveStep,
  type ConnectionDef,
  type ConnectionRef,
  type PolicySpec,
  type ResolvedStep,
  type SlackChannelBinding,
  type StepConfig,
  type WorkflowConfig,
} from '@reef/schema';
import {
  api,
  type ConnectionResourcesResponse,
  type ConnectionSummary,
  type SlackChannelSummary,
} from '../api.ts';
import {
  DUAL_IDENTITY,
  IdentityBadge,
  botLabelFor,
  identityKindFor,
  identityTip,
} from './IdentityBadge.tsx';

/**
 * Everything about one step, in two halves.
 *
 * The top half is what this step *declares* — the editable document. The bottom
 * half is what it will *actually run under* once inheritance is applied. Keeping
 * both on screen is the point: a step whose declared policy looks permissive but
 * whose resolved policy caps it at ten rows is the single most confusing thing
 * about a nested model, and showing the resolved view removes the guesswork.
 *
 * The resolved half is computed here, on the draft, with the same `resolveStep`
 * the runtime uses. It used to be fetched from the resolved endpoint, which
 * reads the *committed* document — so every keystroke re-fetched an answer
 * about a version of the step the reader was no longer looking at, and mounting
 * a connection here showed nothing below until it was committed. The heading
 * says "as drafted" so nobody reads it as what a run today would get.
 */
export function StepInspector({
  step,
  workflow,
  connections,
  policies,
  skills,
  editable,
  dirty,
  onChange,
  onCommit,
}: {
  step: StepConfig;
  /** The in-memory draft, not the committed document — see above. */
  workflow: WorkflowConfig;
  connections: ConnectionSummary[];
  policies: PolicySpec[];
  skills: string[];
  editable: boolean;
  /** Whether the draft differs from what was committed; ⌘Enter commits only then. */
  dirty: boolean;
  onChange(next: StepConfig): void;
  onCommit(): void;
}) {
  const workflowId = workflow.id;
  const patch = (changes: Partial<StepConfig>) => onChange({ ...step, ...changes });

  const drafted = useMemo(() => resolveDraft(workflow, step.id), [workflow, step.id]);

  /*
   * What reaches this step before it declares anything: the workflow's mounts
   * and every ancestor's. Resolved against the parent rather than subtracted
   * from the step's own resolution, because a step that re-declares an
   * inherited mount to pin its actor is legitimately in both lists, and
   * subtracting would hide the half the reader cannot remove.
   */
  const inherited = useMemo<ConnectionRef[]>(() => {
    if (!step.parentId) return workflow.mcpConnections;
    const parent = resolveDraft(workflow, step.parentId);
    return parent.resolved ? parent.resolved.connections : workflow.mcpConnections;
  }, [workflow, step.parentId]);

  const usage = useMemo(() => mountUsage(workflow), [workflow]);
  const selected = step.mcpConnections ?? [];

  const [query, setQuery] = useState('');
  const [picking, setPicking] = useState(false);
  const [mono, setMono] = useState(readMonoPreference);

  const toggleMono = () => {
    const next = !mono;
    setMono(next);
    // A preference about the screen you are sitting at, like the pane widths:
    // stored in the browser rather than committed with the document.
    try {
      localStorage.setItem(MONO_KEY, next ? '1' : '0');
    } catch {
      // Storage refused (private window, blocked site data): the toggle still
      // works for this page and simply is not remembered.
    }
  };

  /**
   * ⌘Enter commits from inside the prompt, where the hands already are. Only
   * when something changed and the reader may commit — otherwise the key does
   * nothing rather than producing a "Saved" that saved nothing, and a reader
   * with `read` never triggers a request the API would refuse.
   */
  const commitOnEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    if (dirty && editable) onCommit();
  };

  const addConnection = (connection: ConnectionSummary) => {
    const taken = new Set([...inherited, ...selected].map((ref) => ref.as));
    const inheritedMount = inherited.find((ref) => ref.id === connection.id);
    /*
     * An inherited connection keeps its mount name, so re-declaring it here
     * pins the actor rather than mounting it twice; anything else gets a name
     * nothing in the chain is using. `resolveStep` refuses to rebind a mount
     * name to a different connection — a child swapping `db` from staging to
     * prod under the parent's instructions — and a picker that produced that
     * collision would turn one click into a red banner about a mount the
     * reader never typed.
     */
    const as = inheritedMount?.as ?? uniqueMount(suggestMount(connection), taken);
    patch({ mcpConnections: [...selected, { id: connection.id, as }] });
    setQuery('');
  };

  const removeConnection = (id: string) => {
    const rest = selected.filter((ref) => ref.id !== id);
    // Absent rather than empty, so a step that adds nothing adds nothing to
    // the committed document either.
    patch({ mcpConnections: rest.length ? rest : undefined });
  };

  const setActor = (id: string, actor: ConnectionRef['actor']) =>
    patch({ mcpConnections: selected.map((ref) => (ref.id === id ? { ...ref, actor } : ref)) });

  /*
   * Absent rather than empty, like `mcpConnections` itself. "Everything the
   * connection allows" and "nothing" are opposite settings, and an empty array
   * in the committed JSON would be a reader's coin toss between them.
   */
  const setScope = (id: string, scope: string[]) =>
    patch({
      mcpConnections: selected.map((ref) =>
        ref.id === id ? { ...ref, scope: scope.length ? scope : undefined } : ref,
      ),
    });

  const needle = query.trim().toLowerCase();
  const candidates = connections.filter(
    (connection) =>
      !selected.some((ref) => ref.id === connection.id) &&
      (!needle ||
        `${connection.name} ${connection.id} ${connection.provider}`.toLowerCase().includes(needle)),
  );
  const groups = PICKER_GROUPS.map((group) => ({
    ...group,
    connections: candidates
      .filter((connection) => pickerGroupFor(connection) === group.key)
      .sort((a, b) => a.name.localeCompare(b.name)),
  })).filter((group) => group.connections.length > 0);
  const showResults = picking || needle.length > 0;

  const promptLines = step.prompt.length === 0 ? 0 : step.prompt.split('\n').length;

  return (
    <div className="stack">
      <div>
        <p className="section-title">Step</p>
        <div className="stack">
          <div className="field">
            <label htmlFor="step-name">Name</label>
            <input
              id="step-name"
              value={step.name}
              disabled={!editable}
              onChange={(event) => patch({ name: event.target.value })}
            />
          </div>

          {/*
            First after the name, because it is the field people open the
            inspector to edit. The textarea has no `rows`: it grows with its
            content and the pane scrolls, so a sixty-line prompt is read in one
            pass rather than through an eight-line window inside a scrolling
            pane — two scrollbars that took turns.
          */}
          <div className="field">
            <label htmlFor="step-prompt">Instructions</label>
            <textarea
              id="step-prompt"
              className={`prompt-editor${mono ? ' mono' : ''}`}
              value={step.prompt}
              disabled={!editable}
              spellCheck={!mono}
              onChange={(event) => patch({ prompt: event.target.value })}
              onKeyDown={commitOnEnter}
            />
            <div className="prompt-meta">
              <span className="hint prompt-count">
                {step.prompt.length.toLocaleString()} chars · {promptLines} line
                {promptLines === 1 ? '' : 's'}
              </span>
              {editable && (
                <span className="hint">
                  <kbd>⌘</kbd>
                  <kbd>↵</kbd> {dirty ? 'commits' : 'commits once something has changed'}
                </span>
              )}
              <button
                type="button"
                className="ghost prompt-mono"
                aria-pressed={mono}
                title={mono ? 'Proportional font' : 'Monospace font'}
                onClick={toggleMono}
              >
                mono
              </button>
            </div>
            <span className="hint">
              This is the agent's system prompt. The code-mode API surface and the policy summary
              are appended automatically.
            </span>
          </div>

          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-runtime">Runtime</label>
              <select
                id="step-runtime"
                value={step.runtime ?? ''}
                disabled={!editable}
                onChange={(event) =>
                  patch({ runtime: (event.target.value || undefined) as StepConfig['runtime'] })
                }
              >
                <option value="">inherit</option>
                <option value="isolate">isolate</option>
                <option value="container">container</option>
              </select>
            </div>

            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-policy">Policy</label>
              <select
                id="step-policy"
                value={typeof step.policy === 'string' ? step.policy : ''}
                disabled={!editable}
                onChange={(event) => patch({ policy: event.target.value || undefined })}
              >
                <option value="">inherit</option>
                {policies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label>Approval</label>
            <label className="row">
              <input
                type="checkbox"
                checked={step.requiresApproval ?? false}
                disabled={!editable}
                onChange={(event) => patch({ requiresApproval: event.target.checked })}
              />
              <span>Pause for a human before committing this step's work</span>
            </label>
            {step.requiresApproval && (
              <>
                <input
                  placeholder="#eng-approvals"
                  value={step.approvalChannel ?? ''}
                  disabled={!editable}
                  onChange={(event) => patch({ approvalChannel: event.target.value })}
                />
                {/*
                  Where else it goes, right beside where the document sends it.
                  Somebody setting "this needs approval" is already deciding who
                  sees it; a separate screen for the second half is a screen
                  they will not find. And the two halves are genuinely
                  different — this one is a row, changeable by the team who
                  reads the channel, where the field above is a commit.
                */}
                <SlackReviewChannels workflowId={workflowId} editable={editable} />
              </>
            )}
          </div>

          {/*
            A picker rather than one chip per connection. Seventeen chips with
            two glyphs each and a legend underneath was a wall the eye could
            not group, and the only question it answered — "is this one
            mounted?" — took a scan of every chip. The selection sits above,
            with the identity decision on the same row as the thing it is
            about; the search below shows what is left, grouped by what stands
            between it and a run.
          */}
          <div className="field">
            <label htmlFor="step-connection-search">Connections this step adds</label>

            {selected.length === 0 ? (
              <span className="hint">None of its own — only what it inherits, below.</span>
            ) : (
              <ul className="mount-list">
                {selected.map((ref) => {
                  const def = connections.find((entry) => entry.id === ref.id);
                  const actor = ref.actor ?? 'auto';
                  const kind = identityKindFor(actor);
                  return (
                    <li key={ref.as} className="mount-chip">
                      <span className="mount-name">{def?.name ?? ref.id}</span>
                      <code className="mount-as" title="The identifier the prompt reaches it by">
                        {ref.as}
                      </code>
                      {/*
                        Only connections that actually hold two credentials get
                        this control. Whose name ends up on a comment is a
                        decision the author makes here, in the document — never
                        something the step's prompt can talk its way into
                        changing. The badge repeats what the select says on
                        purpose: it is the same mark as on the step in the tree
                        and against every audit row, so the three read as one
                        fact rather than three descriptions.
                      */}
                      {def && DUAL_IDENTITY.has(def.credentialMode) && (
                        <>
                          <IdentityBadge
                            kind={kind}
                            label={
                              kind === 'person' ? 'you' : kind === 'bot' ? botLabelFor(def) : 'you or bot'
                            }
                          />
                          <select
                            id={`actor-${ref.id}`}
                            aria-label={`Run ${def.name} as`}
                            title={identityTip(kind, botLabelFor(def))}
                            value={actor}
                            disabled={!editable}
                            onChange={(event) =>
                              setActor(ref.id, event.target.value as ConnectionRef['actor'])
                            }
                          >
                            <option value="auto">
                              whoever triggered it — {botLabelFor(def)} when nobody did
                            </option>
                            <option value="user">the person running it, always</option>
                            <option value="service">{botLabelFor(def)}, always</option>
                          </select>
                        </>
                      )}
                      {editable && (
                        <button
                          type="button"
                          className="chip-x"
                          aria-label={`Remove ${def?.name ?? ref.id}`}
                          onClick={() => removeConnection(ref.id)}
                        >
                          ×
                        </button>
                      )}
                      <ScopePicker
                        connectionId={ref.id}
                        connectionName={def?.name ?? ref.id}
                        scope={ref.scope ?? []}
                        editable={editable}
                        onChange={(next) => setScope(ref.id, next)}
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            {editable && (
              <div
                className="picker"
                onBlur={(event) => {
                  // Closed when focus leaves the picker, not when it moves from
                  // the box to a result — that is the click that adds one.
                  if (!event.currentTarget.contains(event.relatedTarget)) setPicking(false);
                }}
              >
                <input
                  id="step-connection-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Add a connection — search by name"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onFocus={() => setPicking(true)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') return;
                    setQuery('');
                    setPicking(false);
                  }}
                />
                {showResults && (
                  <div className="picker-results">
                    {groups.length === 0 && (
                      <p className="hint picker-empty">
                        {needle ? `Nothing matches "${query.trim()}".` : 'Every connection is already mounted here.'}
                      </p>
                    )}
                    {groups.map((group) => (
                      <div key={group.key} className="picker-group">
                        <p className="picker-group-title">{group.title}</p>
                        {group.connections.map((connection) => (
                          <button
                            type="button"
                            key={connection.id}
                            className="picker-row"
                            onClick={() => addConnection(connection)}
                          >
                            <span className="picker-row-name">
                              {connection.name}
                              <PickerGlyph connection={connection} />
                            </span>
                            <span className="hint">{usageLabel(usage, workflow, connection.id)}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <span className="hint">
              <span aria-hidden="true">⇄</span> marks a connection that can run as you or as a bot;
              pick which on its row once it is mounted.
            </span>
          </div>

          {/*
            Separate and without a remove control, because there is nothing this
            step can do about them: capabilities only widen down the tree, and
            an × here would either lie or rewrite the parent. Shown at all so the
            reader sees the step's whole surface where they are shaping it.
          */}
          {inherited.length > 0 && (
            <div className="field">
              <label>Inherited from the workflow and parents</label>
              <ul className="mount-list">
                {inherited.map((ref) => {
                  const def = connections.find((entry) => entry.id === ref.id);
                  return (
                    <li key={ref.as} className="mount-chip inherited">
                      <span className="mount-name">{def?.name ?? ref.id}</span>
                      <code className="mount-as" title="The identifier the prompt reaches it by">
                        {ref.as}
                      </code>
                      {def && <span className="hint">{actorSentence(ref, def)}</span>}
                    </li>
                  );
                })}
              </ul>
              <span className="hint">
                Always present on this step. Take one off the workflow or the parent step to remove
                it.
              </span>
            </div>
          )}

          <div className="field">
            <label>Skills</label>
            <div className="row wrap">
              {skills.map((skill) => {
                const attached = step.skills?.some((entry) => entry.name === skill);
                return (
                  <button
                    key={skill}
                    className={attached ? 'primary' : ''}
                    disabled={!editable}
                    onClick={() =>
                      patch({
                        skills: attached
                          ? step.skills!.filter((entry) => entry.name !== skill)
                          : [...(step.skills ?? []), { name: skill }],
                      })
                    }
                  >
                    {skill}
                  </button>
                );
              })}
            </div>
          </div>

          {/*
            Network is a widening control, not a narrowing one: resolution takes
            the union of the workflow's allowlist and every ancestor's, so a step
            can reach further than its parent but can never pull an inherited
            host back. The hint says so, because a "none" option here would be a
            switch that silently does nothing.
          */}
          <div className="field">
            <label htmlFor="step-network">Network access</label>
            <input
              id="step-network"
              value={(step.networkAllowlist ?? []).join(', ')}
              placeholder="inherited only — e.g. api.github.com, files.internal"
              disabled={!editable}
              onChange={(event) => {
                const hosts = event.target.value
                  .split(',')
                  .map((entry) => entry.trim())
                  .filter(Boolean);
                // Absent rather than empty, so a step that adds nothing adds
                // nothing to the committed document either.
                patch({ networkAllowlist: hosts.length ? hosts : undefined });
              }}
            />
            <span className="hint">
              Comma-separated hostnames this step may reach directly, on top of whatever the
              workflow and its parent steps already allow. Leave it empty to inherit and nothing
              else — everything then goes through a connection.
            </span>
          </div>

          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-runtime-limit">Max runtime (min)</label>
              <input
                id="step-runtime-limit"
                type="number"
                min={1}
                value={Math.round((step.limits?.maxRuntimeMs ?? 3_600_000) / 60_000)}
                disabled={!editable}
                onChange={(event) =>
                  patch({
                    limits: {
                      maxSpendCents: step.limits?.maxSpendCents ?? 10_000,
                      maxRuntimeMs: Number(event.target.value) * 60_000,
                    },
                  })
                }
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-spend-limit">Max spend ($)</label>
              <input
                id="step-spend-limit"
                type="number"
                min={1}
                value={Math.round((step.limits?.maxSpendCents ?? 10_000) / 100)}
                disabled={!editable}
                onChange={(event) =>
                  patch({
                    limits: {
                      maxRuntimeMs: step.limits?.maxRuntimeMs ?? 3_600_000,
                      maxSpendCents: Number(event.target.value) * 100,
                    },
                  })
                }
              />
            </div>
          </div>
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid var(--border)' }} />

      {/*
        A disclosure rather than the always-open block it replaced. The answer
        is long — connections, tools, chain, limits, model — and it sat under
        the fields people came to edit, so the prompt was off the top of the
        pane before the first keystroke. Uncontrolled on purpose: the open state
        survives switching steps, which is how a reader compares two.
      */}
      <details className="resolved">
        <summary>
          <span className="section-title">Resolved access</span>
          <span className="hint">as drafted</span>
        </summary>

        {drafted.error && (
          <div className="banner danger" style={{ marginTop: '0.5rem' }}>
            <h3>This step cannot be resolved</h3>
            <p style={{ margin: 0 }}>{drafted.error}</p>
          </div>
        )}

        {drafted.resolved && <ResolvedAccess resolved={drafted.resolved} connections={connections} />}
      </details>
    </div>
  );
}

/** The step after inheritance, as the runtime would enforce it. */
function ResolvedAccess({
  resolved,
  connections,
}: {
  resolved: ResolvedStep;
  connections: ConnectionSummary[];
}) {
  return (
    <div className="stack" style={{ marginTop: '0.625rem' }}>
      <div className="row wrap">
        <span className="pill accent">level {resolved.level} of 3</span>
        <span className={`pill ${resolved.runtime === 'container' ? 'warn' : ''}`}>
          {resolved.runtime}
        </span>
        {resolved.requiresApproval && <span className="pill">pauses for approval</span>}
      </div>

      <div className="field">
        <label>Connections</label>
        {resolved.connections.length === 0 ? (
          <span className="hint">None. This step has no data access.</span>
        ) : (
          <ul className="mount-list">
            {resolved.connections.map((ref) => {
              const def = connections.find((entry) => entry.id === ref.id);
              return (
                <li key={ref.as} className="mount-chip">
                  <span className="mount-name">{def?.name ?? ref.id}</span>
                  <code className="mount-as" title="The identifier the prompt reaches it by">
                    {ref.as}
                  </code>
                  <span className="hint">
                    {def ? actorSentence(ref, def) : ''}
                    {ref.tools ? ` · ${ref.tools.length} of its tools` : ''}
                    {/*
                      The resolved scope, spelled out rather than counted. What
                      data a step can reach is the sentence a reviewer is here
                      for, and "2 resources" makes them open the step to find
                      out which two.
                    */}
                    {ref.scope?.length ? ` · ${ref.scope.join(', ')}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/*
        "everything" in words rather than an absent list: no allowlist is the
        widest setting a step has, and an empty field reads as the narrowest.
      */}
      <div className="field">
        <label>Tools</label>
        {resolved.toolsAllowed ? (
          <div className="row wrap">
            {resolved.toolsAllowed.map((tool) => (
              <code key={tool} className="tool">
                {tool}
              </code>
            ))}
          </div>
        ) : (
          <span className="hint">everything the connections expose</span>
        )}
      </div>

      <div className="field">
        <label>Policy chain</label>
        {resolved.policyChain.length === 0 ? (
          <span className="hint">No policy — the workspace defaults apply.</span>
        ) : (
          <ol className="breadcrumb">
            {resolved.policyChain.map((entry, index) => (
              <li key={index}>
                <code>{typeof entry === 'string' ? entry : entry.id}</code>
              </li>
            ))}
          </ol>
        )}
        <span className="hint">
          Merged left to right, always tightening: a child can narrow what its parent allowed but
          never widen it.
        </span>
      </div>

      <div className="field">
        <label>Network</label>
        <span className="hint">
          {resolved.networkAllowlist.length === 0
            ? 'No outbound network. Everything goes through a connection.'
            : resolved.networkAllowlist.join(', ')}
        </span>
      </div>

      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <label>Limits</label>
          <span className="hint">
            ${(resolved.limits.maxSpendCents / 100).toFixed(0)} ·{' '}
            {Math.round(resolved.limits.maxRuntimeMs / 60_000)} min
          </span>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Model</label>
          <span className="hint">{modelLabel(resolved.model)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * What this mount may reach, chosen from what is there.
 *
 * The screen this replaces asked for a resource URL in a textarea, at the
 * moment somebody connected — which is the wrong question in the wrong place.
 * Wrong place, because connecting is an OAuth handshake against a whole
 * account and a grant written then is a guess about workflows not yet written.
 * Wrong question, because for ClickHouse the answer is a uuid: a service is
 * `9a7cae19-a2be-…`, and the grant nobody could type correctly is the only one
 * that matches a real call.
 *
 * So the connection is the credential whole, and the narrowing happens here,
 * beside the prompt that says what the step is for, against a list the server
 * itself provided. Nothing ticked means "everything the connection allows",
 * which is what a mount with no scope has always meant.
 *
 * Some levels are only a way through. The ClickHouse server needs an
 * organization before it will list services, and no grant names an
 * organization — `selectable` says which kind a level is, and it comes from the
 * manifest rather than from a guess here about what the ids look like.
 *
 * The textarea survives as the fallback, because most connectors cannot
 * enumerate themselves and one that can may still be unreachable when the pane
 * is open. It is never the *only* option offered for a connector that has a
 * browser — the failure to avoid is an empty list, which reads as "there is
 * nothing here" rather than "reef could not ask".
 */
function ScopePicker({
  connectionId,
  connectionName,
  scope,
  editable,
  onChange,
}: {
  connectionId: string;
  connectionName: string;
  scope: readonly string[];
  editable: boolean;
  onChange(scope: string[]): void;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ConnectionResourcesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  /*
   * The levels walked into, as `{ key: id }`. The API needs them to call the
   * next tool — `get_services_list` takes the organization the reader clicked —
   * and the trail is what the "up one level" control walks back out of.
   */
  const [trail, setTrail] = useState<Array<{ key: string; id: string; label: string }>>([]);

  const load = useCallback(
    (path: Array<{ key: string; id: string; label: string }>) => {
      setLoading(true);
      const chosen = Object.fromEntries(path.map((entry) => [entry.key, entry.id]));
      api
        .connectionResources(connectionId, path.length, chosen)
        // A picker that cannot list is the textarea, not a broken pane.
        .catch((error: unknown) => ({
          browsable: true,
          level: null,
          selectable: false,
          hasChildren: false,
          resources: [],
          error: error instanceof Error ? error.message : String(error),
        }))
        .then((response: ConnectionResourcesResponse) => {
          setState(response);
          setTrail(path);
        })
        .finally(() => setLoading(false));
    },
    [connectionId],
  );

  useEffect(() => {
    if (open && !state && !loading) load([]);
  }, [open, state, loading, load]);

  const toggle = (value: string) =>
    onChange(
      scope.includes(value) ? scope.filter((entry) => entry !== value) : [...scope, value],
    );

  const summary =
    scope.length === 0
      ? 'everything this connection allows'
      : scope.length === 1
        ? scope[0]!
        : `${scope.length} resources`;

  const listable = state?.browsable && state.resources.length > 0;

  return (
    <details className="mount-scope" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary title={`What ${connectionName} may reach from this step`}>Data · {summary}</summary>

      {loading && <span className="hint">Asking {connectionName} what it has…</span>}

      {trail.length > 0 && (
        <div className="row wrap">
          <button type="button" onClick={() => load(trail.slice(0, -1))}>
            ← {trail.length === 1 ? 'all' : trail[trail.length - 2]!.label}
          </button>
          <span className="hint">{trail.map((entry) => entry.label).join(' / ')}</span>
        </div>
      )}

      {listable && (
        <div className="stack">
          <span className="hint">
            {state.selectable
              ? `${state.level?.label ?? 'Resource'}s on this connection. None ticked means everything it allows; ticking any means only those, and a step below may narrow further but never widen.`
              : `Pick a ${(state.level?.label ?? 'level').toLowerCase()} to see what is inside it. Nothing here is granted on its own.`}
          </span>
          {state.resources.map((entry) => (
            <div key={entry.id} className="row">
              {state.selectable && entry.scope !== null ? (
                <label className="row">
                  <input
                    type="checkbox"
                    disabled={!editable}
                    checked={scope.includes(entry.scope)}
                    onChange={() => toggle(entry.scope!)}
                  />
                  <span>{entry.label}</span>
                </label>
              ) : (
                <span>{entry.label}</span>
              )}
              {state.hasChildren && (
                <button
                  type="button"
                  onClick={() =>
                    load([
                      ...trail,
                      { key: state.level!.key, id: entry.id, label: entry.label },
                    ])
                  }
                >
                  open
                </button>
              )}
              {/*
                The id, shown small and always. It is what lands in the
                committed document, and a reviewer reading a uuid in a diff
                should be able to find it here rather than take it on trust.
              */}
              <code className="hint">{entry.id}</code>
            </div>
          ))}
        </div>
      )}

      {state && !listable && (
        <div className="stack">
          <span className="hint">
            {state.error
              ? `Could not ask ${connectionName} what it has (${state.error}). Write the grants instead — one per line.`
              : `${connectionName} listed nothing here, so the grants are written by hand — one resource URL per line.`}
          </span>
          <textarea
            rows={2}
            spellCheck={false}
            disabled={!editable}
            value={scope.join('\n')}
            style={{ width: '100%', fontFamily: 'ui-monospace, monospace' }}
            onChange={(event) =>
              onChange(event.target.value.split('\n').map((line) => line.trim()).filter(Boolean))
            }
          />
        </div>
      )}
    </details>
  );
}

/** A readable default mount name — `db`, `github` — derived from the connection. */
function suggestMount(connection: ConnectionDef): string {
  const base = connection.provider === 'postgres' ? 'db' : connection.provider;
  return base.replace(/[^a-zA-Z0-9]/g, '').replace(/^[0-9]+/, '') || 'conn';
}

/** `github`, then `github2` — a mount name nothing in the chain is already using. */
function uniqueMount(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}${n}`)) n += 1;
  return `${base}${n}`;
}

/**
 * `resolveStep` on the draft, with the throw turned into a message. It throws
 * for exactly two reasons — an id not in the document, and a child rebinding an
 * inherited mount — and both are things to show beside the fields rather than
 * let take down the pane.
 */
function resolveDraft(
  workflow: WorkflowConfig,
  stepId: string,
): { resolved: ResolvedStep; error: null } | { resolved: null; error: string } {
  try {
    return { resolved: resolveStep(workflow, stepId), error: null };
  } catch (error) {
    return { resolved: null, error: error instanceof Error ? error.message : String(error) };
  }
}

/** How many steps in the draft mount each connection, by connection id. */
function mountUsage(workflow: WorkflowConfig): Map<string, number> {
  const counts = new Map<string, number>();
  const walk = (steps: readonly StepConfig[]) => {
    for (const step of steps) {
      for (const id of new Set((step.mcpConnections ?? []).map((ref) => ref.id))) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
      if (step.childSteps) walk(step.childSteps);
    }
  };
  walk(workflow.steps);
  return counts;
}

/**
 * "used by 2 steps here" — the one line that tells a reader whether mounting
 * this connection matches how the rest of the document does it. A workflow-level
 * mount is called out separately: it reaches every step already, so adding it
 * on one is an actor decision rather than new access.
 */
function usageLabel(usage: Map<string, number>, workflow: WorkflowConfig, id: string): string {
  const steps = usage.get(id) ?? 0;
  const onWorkflow = workflow.mcpConnections.some((ref) => ref.id === id);
  const stepsPart = steps === 0 ? null : `used by ${steps} step${steps === 1 ? '' : 's'} here`;
  if (onWorkflow) return stepsPart ? `on the workflow · ${stepsPart}` : 'on the workflow, so every step here';
  return stepsPart ?? 'not used here yet';
}

type PickerGroup = 'ready' | 'credential' | 'builtin';

/**
 * Three groups, by whether the connection can run today. The Connections
 * screen splits the middle one by who can fix it; here the reader is choosing
 * what a step reaches, not fixing anything, and "needs a credential" is the
 * whole of what they need to know before mounting it. `authorized` is the same
 * answer the old ⚠ glyph gave, moved from a mark on every chip to a heading
 * over the ones it applies to.
 */
function pickerGroupFor(connection: ConnectionSummary): PickerGroup {
  if (!connection.usesUser && !connection.usesBot) return 'builtin';
  return connection.authorized ? 'ready' : 'credential';
}

const PICKER_GROUPS: ReadonlyArray<{ key: PickerGroup; title: string }> = [
  { key: 'ready', title: 'Ready' },
  { key: 'credential', title: 'Needs a credential' },
  { key: 'builtin', title: 'Built in' },
];

/**
 * The identity a connection runs as, before it is mounted. ⇄ for the ones that
 * have a choice to make — the same mark the legend explains — and the settled
 * identity for the ones that do not; a built-in server has no identity to show.
 */
function PickerGlyph({ connection }: { connection: ConnectionSummary }) {
  if (DUAL_IDENTITY.has(connection.credentialMode)) {
    return (
      <span
        className="picker-glyph"
        role="img"
        aria-label="two identities"
        title={`Can run as you or as ${botLabelFor(connection)} — mount it to choose.`}
      >
        ⇄
      </span>
    );
  }
  if (connection.credentialMode === 'user_oauth') return <IdentityBadge kind="person" label="you" />;
  if (connection.credentialMode === 'service') {
    return <IdentityBadge kind="bot" label={connection.serviceAccountLabel ?? 'bot'} />;
  }
  return null;
}

/** "as you", "as Reef Bot", "as you, or Reef Bot unattended" — for a mount in a list. */
function actorSentence(ref: ConnectionRef, def: ConnectionSummary): string {
  const bot = botLabelFor(def);
  if (!DUAL_IDENTITY.has(def.credentialMode)) {
    if (def.credentialMode === 'user_oauth') return 'as you';
    if (def.credentialMode === 'service') return `as ${bot}`;
    return 'built in';
  }
  if (ref.actor === 'user') return 'as you';
  if (ref.actor === 'service') return `as ${bot}`;
  return `as you, or ${bot} unattended`;
}

/** The step's own model choice, unresolved — resolution needs the acting person's preference. */
function modelLabel(model: ResolvedStep['model']): string {
  if (model === null) return 'deployment default';
  if (typeof model === 'string') return model;
  const name = [model.provider, model.model].filter(Boolean).join('/') || 'deployment default';
  return model.effort ? `${name} · ${model.effort} effort` : name;
}

const MONO_KEY = 'reef.builder.prompt-mono';

function readMonoPreference(): boolean {
  try {
    return localStorage.getItem(MONO_KEY) === '1';
  } catch {
    return false;
  }
}



/**
 * The channels this workflow's reviews *also* reach.
 *
 * The field above it is `approvalChannel` — one string, committed to git,
 * changeable only by somebody with edit rights on the workflow. That is the
 * right bar for what a step may do and the wrong one for where it talks: the
 * team who wants the channel changed is usually not the team who can commit,
 * so the request sits in a backlog while approvals land somewhere nobody reads.
 *
 * A binding is a row instead. It shows here because this is where the decision
 * is being made, and it says plainly that anybody standing in a channel can add
 * one with `/reef connect` — which is the path that does not need this screen
 * at all.
 */
function SlackReviewChannels({
  workflowId,
  editable,
}: {
  workflowId: string;
  editable: boolean;
}) {
  const [bindings, setBindings] = useState<SlackChannelBinding[]>([]);
  const [channels, setChannels] = useState<SlackChannelSummary[]>([]);
  const [configured, setConfigured] = useState(true);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const settings = await api.slackSettings(workflowId);
      setBindings(settings.bindings);
      setConfigured(settings.configured);
      if (settings.configured) {
        setChannels((await api.listSlackBotChannels()).channels);
      }
    } catch {
      // Silent: this is a secondary control on somebody else's screen, and a
      // red banner here for a Slack that is simply absent would read as a
      // problem with the step they are editing.
      setConfigured(false);
    }
  }, [workflowId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!configured) return null;

  const relevant = bindings.filter(
    (binding) => binding.purpose === 'all' || binding.purpose === 'approvals',
  );

  const add = async () => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      const channel = channels.find((entry) => entry.id === choice);
      await api.bindSlackChannel({
        channelId: choice,
        ...(channel ? { channelName: channel.name } : {}),
        workflowId,
        purpose: 'approvals',
      });
      setChoice('');
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (binding: SlackChannelBinding) => {
    setBusy(true);
    setError(null);
    try {
      await api.unbindSlackChannel(binding.channelId, binding.workflowId);
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ marginTop: '0.5rem' }}>
      <p className="hint" style={{ margin: 0 }}>
        Also posted to
        {relevant.length === 0 && ' — nothing yet.'}
      </p>

      {relevant.length > 0 && (
        <div className="row wrap">
          {relevant.map((binding) => (
            <span className="chip" key={binding.id}>
              {binding.channelName ? `#${binding.channelName}` : binding.channelId}
              {/*
                A workspace-wide binding is shown here because it does reach
                this workflow — but it is not this workflow's to remove, and
                a Remove button that 403s would be worse than none.
              */}
              {binding.workflowId === null ? (
                <em style={{ marginLeft: '0.35rem', fontStyle: 'normal', opacity: 0.7 }}>
                  (all workflows)
                </em>
              ) : (
                editable && (
                  <button
                    className="chip-x"
                    onClick={() => void remove(binding)}
                    disabled={busy}
                    aria-label={`Stop posting to ${binding.channelName ?? binding.channelId}`}
                  >
                    ×
                  </button>
                )
              )}
            </span>
          ))}
        </div>
      )}

      {editable && (
        <div className="row">
          <select value={choice} onChange={(event) => setChoice(event.target.value)}>
            <option value="">add a channel…</option>
            {channels
              .filter((channel) => !relevant.some((binding) => binding.channelId === channel.id))
              .map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.private ? '🔒 ' : '# '}
                  {channel.name}
                </option>
              ))}
          </select>
          <button className="ghost" onClick={() => void add()} disabled={!choice || busy}>
            Add
          </button>
        </div>
      )}

      {/* `--danger` rather than a `.banner`: this is a secondary control inside
          a field, and a full-width error block here would out-shout the step
          being edited. */}
      {error && (
        <p className="hint" style={{ margin: 0, color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      <p className="hint" style={{ margin: 0 }}>
        Anyone can add the channel they are standing in with <code>/reef connect</code>, and a
        reviewer can have their own copy DM'd with <code>/reef notify</code>. A decision made
        anywhere closes the card everywhere.
      </p>
    </div>
  );
}
